import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Input, Skeleton, Space } from 'antd';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as IconNote } from 'assets/images/icons/iconNote.svg';
import moment from 'moment';
import * as React from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes';
import { turnDrawerOff } from 'store/app/actions';
import { ActivityType, DataLogStatus } from 'store/app/enums';
import { fetchActivityLogsRequest } from 'store/vat/actions';
import { ActivityLog, VatRunData } from 'store/vat/models';
import { getVatActivityLogs, getVatLoading } from 'store/vat/selectors';
import styled from 'styled-components';
import { RenderUserDetail } from 'utils/render-user-details';
import { sanitizeActivityLogHtml } from 'utils/sanitize-html';
import { StatusGetter } from 'utils/status-getter';

const ActivityLogsWrapper = styled.div`
  .activity-card {
    border-radius: 10px;
    opacity: 0px;
    background: var(--color-surface-raised, #ffffff);
    border: 1px solid #e9f6f7;
    padding: 15px 20px;
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
  }
  .title-date {
    font-family: Inter;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
    color: var(--color-text-dark, #042a2a);
    border-bottom: 1px solid var(--color-border, #dbeae3);
    padding-bottom: 10px;
    margin-bottom: 10px;
  }
  .user-box {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
  }
  .user-details {
    display: flex;
    flex-direction: column;
    font-family: Inter;
    font-weight: 400;
  }
  .user-name {
    font-size: 14px;
    color: var(--color-text-dark, #042a2a);
  }
  .user-email {
    font-size: 12px;
    color: var(--color-text-muted, #5a6a6a);
  }
  .note-log {
    border-radius: 10px;
    opacity: 0px;
    border: 1px solid #e9f6f7;
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    padding: 15px;
    margin-top: 10px;
  }
`;

const { TextArea } = Input;

interface ActivityLogsProps {
  data: VatRunData;
  activityLogs?: ActivityLog[];
  updateNote: (noteText: string, dataLogId: string) => void;
  connectionId: string;
  mappingId: string;
  /** When true, show Add Note and Export Activity buttons even when status is SUBMITTED or missing (e.g. Excel organisation activity). */
  showAddNote?: boolean;
}

export const ActivityLogs: React.FC<ActivityLogsProps> = ({
  data,
  activityLogs: propActivityLogs,
  updateNote,
  connectionId,
  mappingId,
  showAddNote = false,
}) => {
  const { t } = useTranslation();
  const [editNote, setEditNote] = React.useState(false);
  const [noteText, setNoteText] = React.useState<string>('');
  const dispatch = useDispatch();
  const storeActivityLogs = useSelector(getVatActivityLogs);
  const loading = useSelector(getVatLoading);

  // Use store activity logs if prop is not provided, otherwise use prop
  const activityLogs = propActivityLogs || storeActivityLogs;
  const isLoading = loading && !activityLogs;

  /**
   * When parent does not pass `activityLogs`, load from the VAT API whenever this
   * instance mounts or `dataLogId` changes (e.g. reconciliation drawer re-opened).
   * Do not skip when Redux still has logs from a previous return — that was stale UI.
   * Parents that pass `activityLogs` (incl. `[]`) own fetching (VAT activity tab, Excel drawer).
   */
  React.useEffect(() => {
    if (propActivityLogs !== undefined) return;
    if (!data?.dataLogId) return;
    dispatch(fetchActivityLogsRequest(data.dataLogId));
  }, [data?.dataLogId, dispatch, propActivityLogs]);

  const onNoteChange = (e) => {
    setNoteText(e.target.value);
  };

  const onNoteSave = () => {
    updateNote(noteText, data.dataLogId);
    noteText !== '' && setEditNote(false);
    setNoteText('');
    dispatch(turnDrawerOff());
  };
  // const columns = [
  //   {
  //     title: 'Changes',
  //     dataIndex: 'type',
  //     render: (content) => ActivityType[content],
  //   },
  //   {
  //     title: 'Date',
  //     dataIndex: 'createdAt',
  //     render: (content) => moment(content).format('DD/MM/YYYY - hh:mm:ss'),
  //   },
  //   {
  //     title: 'User',
  //     dataIndex: 'fullName',
  //   },
  //   {
  //     title: 'Email',
  //     dataIndex: 'email',
  //   },
  //   {
  //     title: 'Details',
  //     dataIndex: 'details',
  //     render: (content: string) => {
  //       if (content.includes('<') && content.includes('>'))
  //         return <div dangerouslySetInnerHTML={{ __html: content }} />;
  //       return content;
  //     },
  //   },
  // ];

  const getDetails = (logFile: ActivityLog) => {
    const content = logFile.details;
    const name = logFile.fullName;

    if (content.includes('<') && content.includes('>'))
      if (
        content.includes('<submissionlink>') &&
        content.includes('</submissionlink>')
      ) {
        const mainText = content.split('<submissionlink>')[0];
        const linkText = content
          .split('<submissionlink>')[1]
          .split('</submissionlink>')[0];
        const hrefString = routes.vat.viewReportLink
          .replace(':connectionId', connectionId)
          .replace(':mappingId', mappingId)
          .replace(
            ':dataLogId',
            logFile?.linkedPeriodId ? logFile?.linkedPeriodId : ''
          );

        const htmlString = `<span>${mainText}</span><a href="${hrefString}">${linkText}</a>`;
        return (
          <div
            //className="note-log"
            dangerouslySetInnerHTML={{
              __html: sanitizeActivityLogHtml(htmlString),
            }}
          />
        );
      } else {
        const noteHtml = content
          .replace(
            t('vat-report.note-subject-label'),
            `<strong>${t('vat-report.note-subject-label')}</strong>`
          )
          .replace(
            t('vat-report.note-message-label'),
            `<strong>${t('vat-report.note-message-label')}</strong>`
          )
          .replace(
            t('vat-report.note-preferences-label'),
            `<strong>${t('vat-report.note-preferences-label')}</strong>`
          );
        return (
          <>
            <span>{t('vat-report.sent-note-to-client', { name })}</span>
            <div
              className="note-log"
              dangerouslySetInnerHTML={{
                __html: sanitizeActivityLogHtml(noteHtml),
              }}
            />
          </>
        );
      }
    return content;
  };

  const onExportActivities = () => {
    activityLogs;
  };

  const getDataForExport = (activityLogs?: ActivityLog[]) => {
    if (!activityLogs || activityLogs.length === 0) {
      return [];
    }

    return activityLogs.map((log) => ({
      type: ActivityType[log.type],
      createdAt: moment(log.createdAt).format('DD/MM/YYYY - hh:mm:ss'),
      details: log.details,
      email: log.email,
      fullName: log.fullName,
    }));
  };

  const getHeaderForExport = (activityLogs?: ActivityLog[]) => {
    if (!activityLogs || activityLogs.length === 0) {
      return [];
    }

    // Define headers based on the structure used in getDataForExport
    const headers = ['type', 'createdAt', 'details', 'email', 'fullName'];
    return headers.map((key) => ({
      label: key.replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
      key: key,
    }));
  };

  const renderActivitySkeleton = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <div key={`skeleton-${index}`} className="activity-card">
        <div style={{ alignSelf: 'end', marginBottom: 10 }}>
          <Skeleton.Button
            active
            size="small"
            style={{ width: 80, height: 20 }}
          />
        </div>
        <Skeleton.Input
          active
          size="small"
          style={{ width: 180, height: 14, marginBottom: 10 }}
        />
        <div className="user-box">
          <Skeleton.Avatar active size="default" shape="circle" />
          <div style={{ flex: 1 }}>
            <Skeleton.Input
              active
              size="small"
              style={{ width: 120, height: 14, marginBottom: 8 }}
            />
            <Skeleton.Input
              active
              size="small"
              style={{ width: 150, height: 12 }}
            />
          </div>
        </div>
        <Skeleton
          active
          paragraph={{ rows: 2, width: ['100%', '80%'] }}
          title={false}
          style={{ marginTop: 10 }}
        />
      </div>
    ));
  };

  return (
    <ActivityLogsWrapper>
      {(showAddNote ||
        (data?.status &&
          DataLogStatus[data?.status] !== DataLogStatus.SUBMITTED)) && (
        <Space size={'large'}>
          <ExportButton
            icon={<IconNote style={{ width: 20, height: 20 }} />}
            style={{ marginBottom: 20 }}
            onClick={() => setEditNote(!editNote)}
          >
            {!editNote ? t('common.add-note') : t('organisation.reconciliation-close-note')}
          </ExportButton>
          <ExportButton
            icon={<IconExport style={{ width: 20, height: 20 }} />}
            style={{ marginBottom: 20 }}
            onClick={onExportActivities}
          >
            <CSVLink
              filename={'activity logs.csv'}
              data={getDataForExport(activityLogs)}
              headers={getHeaderForExport(activityLogs)}
            >
              <span>{t('vat-report.export-activity')}</span>
            </CSVLink>
          </ExportButton>
        </Space>
      )}

      {editNote && (
        <>
          <TextArea rows={4} onChange={onNoteChange} value={noteText} />
          <div className="divider-20" />
          <ScopeButton
            type={'primary'}
            onClick={onNoteSave} //@ts-ignore
            width="100%"
          >
            {t('organisation.reconciliation-save-note')}
          </ScopeButton>
          <div className="divider-20" />
        </>
      )}
      <div
        className={`scroller-activity ${editNote && 'scroller-activity-note'}`}
      >
        {isLoading ? (
          renderActivitySkeleton()
        ) : activityLogs && activityLogs.length > 0 ? (
          activityLogs.map((al, index) => (
            <div
              key={`${al.createdAt}-${al.type}-${index}`}
              className="activity-card"
            >
              <div style={{ alignSelf: 'end', marginBottom: 10 }}>
                <StatusGetter status={al.type} smaller />
              </div>
              <span className="title-date">
                {moment(al.createdAt).format('DD/MM/YYYY - hh:mm:ss')}
              </span>

              <div className="user-box">
                <RenderUserDetail record={al.userDto} showOption />
              </div>
              <div>{getDetails(al)}</div>
            </div>
          ))
        ) : (
          <div
            style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-muted, #5a6a6a)' }}
          >
            {t('vat-report.no-activity-logs-available')}
          </div>
        )}
      </div>
    </ActivityLogsWrapper>
  );
};
